import { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";

(() => {
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Fredoka+One&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.textContent = `
    *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
    body{margin:0;background:#f0f6ff;}
    input,textarea,button,select{font-family:'Nunito',sans-serif;}
    @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
    @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
    .fade-up{animation:fadeUp .4s cubic-bezier(.22,1,.36,1) both}
    .float{animation:float 3s ease-in-out infinite}
    .spin{animation:spin 1s linear infinite}
    ::-webkit-scrollbar{width:4px}
    ::-webkit-scrollbar-track{background:transparent}
    ::-webkit-scrollbar-thumb{background:#b3d0f0;border-radius:2px}
    button:active{transform:scale(0.97);}
  `;
  document.head.appendChild(s);
})();

const G = {
  blue:    "#1565C0",
  blue2:   "#1976D2",
  blue3:   "#E3F0FF",
  blueD:   "#0D47A1",
  gold:    "#F5A623",
  gold2:   "#FFB74D",
  goldD:   "#E65100",
  orange:  "#FF6D00",
  white:   "#FFFFFF",
  cream:   "#FFFBF2",
  ink:     "#0D2A5E",
  ink2:    "#2C4A8A",
  ink3:    "#7B9CC8",
  bg:      "#EEF5FF",
  card:    "#FFFFFF",
  line:    "#D0E4F7",
  green:   "#2E7D32",
  red:     "#C62828",
  teal:    "#00695C",
  purple:  "#5E35B1",
  amber:   "#E65100",
  display: "'Fredoka One', cursive",
  body:    "'Nunito', sans-serif",
};

// Gradient helpers
const heroGrad = `linear-gradient(135deg, ${G.blue} 0%, ${G.blue2} 60%, #1E88E5 100%)`;
const goldGrad  = `linear-gradient(135deg, ${G.gold} 0%, ${G.gold2} 100%)`;
const btnGrad   = `linear-gradient(135deg, ${G.gold} 0%, ${G.orange} 100%)`;
const cardShadow = "0 4px 20px rgba(21,101,192,0.12)";
const cardShadowHover = "0 8px 32px rgba(21,101,192,0.22)";

const UNIVERSE = [
  {
    id:"legal", label:"Family Law", icon:"⚖️", color:G.blue, bg:"#E3F0FF",
    desc:"Custody, visitation & court documents",
    subtypes:[
      { id:"family-court", label:"Family Court", icon:"👨‍👩‍👧", desc:"Custody, visitation & parenting",
        cats:[
          {id:"ns",  label:"Didn't Show Up",    icon:"✗",  color:G.red},
          {id:"lt",  label:"Late Arrival",       icon:"🕐", color:G.amber},
          {id:"va",  label:"Verbal Abuse",       icon:"⚠️", color:G.purple},
          {id:"ok",  label:"Showed Up On Time",  icon:"✓",  color:G.green},
          {id:"nc",  label:"No Communication",   icon:"🔕", color:G.ink2},
          {id:"cv",  label:"Order Violation",    icon:"📋", color:G.purple},
          {id:"ep",  label:"Early Pickup",       icon:"↩️", color:G.amber},
          {id:"du",  label:"Drug/Alcohol Use",   icon:"⚠️", color:G.red},
        ]},
      { id:"landlord", label:"Landlord / Tenant", icon:"🏠", desc:"Repairs, violations & lease",
        cats:[
          {id:"rr",   label:"Repair Request",         icon:"🔧", color:G.amber},
          {id:"ri",   label:"Request Ignored",         icon:"✗",  color:G.red},
          {id:"vl",   label:"Code Violation",          icon:"⚠️", color:G.purple},
          {id:"rp",   label:"Rent Payment",            icon:"💰", color:G.green},
          {id:"evict",label:"Eviction Notice",         icon:"📄", color:G.red},
          {id:"lc",   label:"Landlord Communication",  icon:"💬", color:G.ink2},
        ]},
      { id:"workplace", label:"Workplace / HR", icon:"🏢", desc:"Harassment & discrimination",
        cats:[
          {id:"hi",  label:"Harassment Incident", icon:"⚠️", color:G.red},
          {id:"hr",  label:"HR Report Filed",      icon:"📁", color:G.blue},
          {id:"rt",  label:"Retaliation",          icon:"🔄", color:G.purple},
          {id:"ci",  label:"Complaint Ignored",    icon:"✗",  color:G.ink2},
          {id:"disc",label:"Discrimination",       icon:"⚖️", color:G.red},
        ]},
      { id:"other-legal", label:"Other Legal", icon:"📋", desc:"General legal documentation",
        cats:[
          {id:"filing",  label:"Filing",          icon:"📁", color:G.blue},
          {id:"hearing", label:"Hearing / Court", icon:"⚖️", color:G.blueD},
          {id:"comm",    label:"Communication",   icon:"✉️", color:G.ink2},
          {id:"deadline",label:"Deadline",        icon:"⏰", color:G.red},
          {id:"payment", label:"Payment / Fee",   icon:"💰", color:G.amber},
        ]},
    ]
  },
  {
    id:"health", label:"Health", icon:"🩺", color:"#00695C", bg:"#E0F2F1",
    desc:"Medical records, symptoms & care",
    subtypes:[
      { id:"general-health", label:"General Health", icon:"🏥", desc:"Doctor visits & records",
        cats:[
          {id:"appt",    label:"Appointment",      icon:"📅", color:"#00695C"},
          {id:"symptom", label:"Symptom",          icon:"🤒", color:G.red},
          {id:"med",     label:"Medication",       icon:"💊", color:G.blue},
          {id:"test",    label:"Test / Lab",       icon:"🧪", color:G.purple},
          {id:"expense", label:"Medical Expense",  icon:"💰", color:G.red},
        ]},
      { id:"mental-health", label:"Mental Health", icon:"🧠", desc:"Therapy, mood & wellness",
        cats:[
          {id:"mood",    label:"Mood Log",         icon:"😊", color:G.purple},
          {id:"therapy", label:"Therapy Session",  icon:"💬", color:G.teal},
          {id:"med",     label:"Medication",       icon:"💊", color:G.blue},
          {id:"trigger", label:"Trigger",          icon:"⚠️", color:G.red},
          {id:"win",     label:"Positive Moment",  icon:"✨", color:G.green},
        ]},
    ]
  },
  {
    id:"home", label:"Home", icon:"🏡", color:"#E65100", bg:"#FFF3E0",
    desc:"Repairs, maintenance & home management",
    subtypes:[
      { id:"repairs", label:"Repairs & Maintenance", icon:"🔧", desc:"Track repairs & contractors",
        cats:[
          {id:"repair",    label:"Repair Needed",    icon:"🔧", color:G.amber},
          {id:"complete",  label:"Repair Complete",  icon:"✓",  color:G.green},
          {id:"cost",      label:"Cost / Invoice",   icon:"💰", color:G.red},
          {id:"contractor",label:"Contractor",       icon:"👷", color:G.ink2},
        ]},
    ]
  },
  {
    id:"finance", label:"Finance", icon:"💰", color:G.green, bg:"#E8F5E9",
    desc:"Money management & financial goals",
    subtypes:[
      { id:"budget", label:"Budget & Expenses", icon:"💸", desc:"Track spending",
        cats:[
          {id:"expense", label:"Expense",      icon:"💸", color:G.red},
          {id:"income",  label:"Income",       icon:"💰", color:G.green},
          {id:"bill",    label:"Bill",         icon:"📋", color:G.amber},
          {id:"sub",     label:"Subscription", icon:"🔄", color:G.blue},
        ]},
    ]
  },
  {
    id:"work", label:"Work", icon:"💼", color:G.blue2, bg:"#E3F2FD",
    desc:"Career, projects & professional life",
    subtypes:[
      { id:"job-log", label:"Job Log", icon:"💼", desc:"Daily work & achievements",
        cats:[
          {id:"project", label:"Project",          icon:"📁", color:G.blue},
          {id:"win",     label:"Win / Achievement", icon:"🏆", color:G.gold},
          {id:"issue",   label:"Issue / Problem",  icon:"⚠️", color:G.red},
          {id:"meeting", label:"Meeting",          icon:"📅", color:G.ink2},
        ]},
    ]
  },
  {
    id:"custom", label:"Custom", icon:"✨", color:G.gold, bg:"#FFFDE7",
    desc:"Your own binder — anything you need",
    subtypes:[
      { id:"blank", label:"Start Blank", icon:"📄", desc:"Build your own from scratch",
        cats:[
          {id:"note",  label:"Note",  icon:"📝", color:G.gold},
          {id:"item",  label:"Item",  icon:"📌", color:G.ink2},
          {id:"task",  label:"Task",  icon:"✓",  color:G.green},
          {id:"event", label:"Event", icon:"📅", color:G.blue},
        ]},
      { id:"vehicle", label:"Vehicle", icon:"🚗", desc:"Car maintenance & costs",
        cats:[
          {id:"service",   label:"Service",      icon:"🔧", color:G.amber},
          {id:"repair",    label:"Repair",       icon:"🔧", color:G.red},
          {id:"fuel",      label:"Fuel/Mileage", icon:"⛽", color:G.blue},
          {id:"insurance", label:"Insurance",    icon:"📋", color:G.purple},
        ]},
    ]
  },
];

const TYPE_TABS = {
  legal:   ["Journal","Case Log","Evidence","Charts","Voice→Doc","Report","Settings"],
  health:  ["Journal","Symptoms","Files","Voice→Doc","Report","Settings"],
  home:    ["Journal","Repairs","Files","Voice→Doc","Report","Settings"],
  finance: ["Journal","Expenses","Files","Voice→Doc","Report","Settings"],
  work:    ["Journal","Projects","Files","Voice→Doc","Report","Settings"],
  custom:  ["Journal","Log","Files","Voice→Doc","Report","Settings"],
};

const TAB_ROLE = {
  "Journal":"journal","Voice→Doc":"voice","Report":"report","Settings":"settings",
  "Case Log":"log","Symptoms":"log","Repairs":"log","Expenses":"log","Projects":"log","Log":"log",
  "Evidence":"evidence","Files":"evidence",
  "Charts":"charts",
};

const TAB_META = {
  "Case Log":  {lbl:"Incident Type",   ph:"Describe what happened, who was present, what was said…"},
  "Symptoms":  {lbl:"Symptom Type",    ph:"Describe the symptom — severity, duration, triggers…"},
  "Repairs":   {lbl:"Repair Type",     ph:"What is broken, when did it start…"},
  "Expenses":  {lbl:"Expense Type",    ph:"Amount, vendor, what it was for…"},
  "Projects":  {lbl:"Project Type",    ph:"Project name, status, blockers…"},
  "Log":       {lbl:"Category",        ph:"Describe this entry…"},
};

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2,5);
const mem = {};
const db = { get:(k,d)=>mem[k]!==undefined?mem[k]:d, set:(k,v)=>{mem[k]=v;}, del:(k)=>{delete mem[k];} };
const fd  = d=>{ try{return new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});}catch{return d||"";} };
const fdt = d=>{ try{return new Date(d).toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"});}catch{return d||"";} };

async function callAI(messages, system="") {
  const r = await fetch("https://api.anthropic.com/v1/messages",{
    method:"POST",headers:{"Content-Type":"application/json"},
    body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1500,messages,...(system?{system}:{})})
  });
  const d = await r.json();
  return d.content?.map(i=>i.text||"").join("")||"";
}
const parseJ = t=>{ try{return JSON.parse(t.replace(/```json|```/g,"").trim());}catch{return null;} };

async function voiceToDoc(raw, type, title, entries) {
  const ctx = entries.slice(0,8).map(e=>`- ${e.cat}: ${e.text}`).join("\n")||"No prior entries.";
  return callAI([{role:"user",content:`Convert this into a professional ${type} document.\nBinder: "${title}"\nContext:\n${ctx}\nStatement:\n"${raw}"\n\nWrite a clear heading, narrative paragraph, key facts as bullets, and a "Notes for Review" section. Plain text only.`}],
    "You convert personal narratives into professional legal/family documents. Be formal and precise.");
}
async function askAI(q, entries, type, title) {
  const s = entries.slice(0,15).map(e=>`${e.cat}: ${e.text||""}`).join("\n");
  return callAI([{role:"user",content:`Binder: "${title}" (${type})\nEntries:\n${s}\n\nQuestion: ${q}`}],
    "You analyze life binder data. Be helpful, factual, concise.");
}

// ── Shared styles ──
const S = {
  page:  { fontFamily:G.body, background:G.bg, minHeight:"100vh", color:G.ink, maxWidth:440, margin:"0 auto", paddingBottom:100 },
  card:  { background:G.card, borderRadius:20, padding:18, marginBottom:12, boxShadow:cardShadow, border:`1.5px solid ${G.line}` },
  sec:   { padding:"18px 16px 0" },
  lbl:   { fontSize:10, letterSpacing:1.5, color:G.ink3, textTransform:"uppercase", marginBottom:8, display:"block", fontWeight:800 },
  inp:   { background:"#F5F9FF", border:`2px solid ${G.line}`, borderRadius:12, color:G.ink, padding:"11px 14px", fontSize:13, width:"100%", outline:"none", fontFamily:G.body, fontWeight:600 },
  txa:   { background:"#F5F9FF", border:`2px solid ${G.line}`, borderRadius:12, color:G.ink, padding:"11px 14px", fontSize:13, width:"100%", outline:"none", resize:"vertical", minHeight:100, fontFamily:G.body, fontWeight:600 },
  div:   { border:"none", borderTop:`2px dashed ${G.line}`, margin:"20px 0" },
  btnGold: { background:btnGrad, color:G.white, border:"none", borderRadius:14, padding:"13px 20px", fontSize:14, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", gap:7, boxShadow:"0 4px 14px rgba(230,81,0,0.3)", fontFamily:G.body },
  btnBlue: { background:heroGrad, color:G.white, border:"none", borderRadius:14, padding:"13px 20px", fontSize:14, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", gap:7, boxShadow:"0 4px 14px rgba(21,101,192,0.3)", fontFamily:G.body },
  btnGhost:{ background:G.white, color:G.blue, border:`2px solid ${G.line}`, borderRadius:14, padding:"11px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:7, fontFamily:G.body },
  tab:  (a,c=G.blue) => ({ flex:1, padding:"10px 0", textAlign:"center", fontSize:9.5, letterSpacing:0.3, fontWeight:800, background:"none", border:"none", cursor:"pointer", color:a?c:G.ink3, borderBottom:a?`3px solid ${c}`:"3px solid transparent", whiteSpace:"nowrap", transition:"all .15s", fontFamily:G.body }),
  badge:(c)=>({ display:"inline-flex", alignItems:"center", background:c+"22", color:c, border:`1.5px solid ${c}33`, borderRadius:8, padding:"3px 9px", fontSize:10, fontWeight:800, gap:3 }),
};

// ── Logo SVG ──
function Logo({ size=28 }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:8}}>
      <div style={{width:size+8,height:size+8,background:btnGrad,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.8,boxShadow:"0 3px 10px rgba(230,81,0,0.35)"}}>🛡️</div>
      <div>
        <div style={{fontFamily:G.display,fontSize:size*0.85,color:G.white,lineHeight:1,letterSpacing:0.3}}>GOTCHA</div>
        <div style={{fontFamily:G.display,fontSize:size*0.85,color:G.gold,lineHeight:1,letterSpacing:0.3}}>GUARD</div>
      </div>
    </div>
  );
}

// ── Wave divider ──
function Wave({ color="#EEF5FF", flip=false }) {
  return (
    <svg viewBox="0 0 440 40" style={{display:"block",width:"100%",transform:flip?"scaleY(-1)":"none",marginBottom:-1}} preserveAspectRatio="none">
      <path d="M0,20 C80,40 160,0 220,20 C280,40 360,0 440,20 L440,40 L0,40 Z" fill={color}/>
    </svg>
  );
}

// ── PIN Screen ──
function Pin({ onUnlock, isSetup }) {
  const [pin,setPin]=useState("");
  const [cnf,setCnf]=useState("");
  const [step,setStep]=useState("enter");
  const [err,setErr]=useState("");

  function press(d) {
    if (isSetup) {
      if (step==="enter") { const np=pin+d; setPin(np); if(np.length===4)setStep("confirm"); }
      else { const nc=cnf+d; setCnf(nc); if(nc.length===4){if(nc===pin){db.set("gg_pin",pin);onUnlock();}else{setErr("PINs don't match");setCnf("");setPin("");setStep("enter");}}}
    } else {
      const np=pin+d; setPin(np); if(np.length===4){if(np===db.get("gg_pin",null))onUnlock();else{setErr("Wrong PIN — try again");setPin("");}}
    }
  }

  const cur = step==="enter"?pin:cnf;
  return (
    <div style={{position:"fixed",inset:0,background:heroGrad,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
      <div style={{position:"absolute",top:-60,left:"50%",transform:"translateX(-50%)",width:260,height:260,borderRadius:"50%",background:"rgba(255,255,255,0.04)"}}/>
      <div className="float" style={{marginBottom:28,textAlign:"center"}}>
        <div style={{width:80,height:80,background:btnGrad,borderRadius:24,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",fontSize:40,boxShadow:"0 8px 24px rgba(230,81,0,0.4)"}}>🛡️</div>
        <div style={{fontFamily:G.display,fontSize:30,color:G.white,letterSpacing:0.5}}>GOTCHA GUARD</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",marginTop:4,fontWeight:600}}>
          {isSetup?(step==="enter"?"Set your security PIN":"Confirm your PIN"):"Enter your PIN to continue"}
        </div>
      </div>
      <div style={{display:"flex",gap:14,marginBottom:10}}>
        {[0,1,2,3].map(i=><div key={i} style={{width:14,height:14,borderRadius:"50%",border:"2.5px solid rgba(255,255,255,0.5)",background:cur.length>i?"#fff":"transparent",transition:"all .2s"}}/>)}
      </div>
      <div style={{height:20,color:G.gold2,fontSize:12,fontWeight:800,marginBottom:24}}>{err}</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,width:240}}>
        {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((k,i)=>(
          k===""?<div key={i}/>:
          <button key={i} onClick={()=>k==="⌫"?(step==="enter"?setPin(p=>p.slice(0,-1)):setCnf(p=>p.slice(0,-1))):press(String(k))}
            style={{height:62,background:k==="⌫"?"rgba(255,255,255,0.1)":"rgba(255,255,255,0.18)",backdropFilter:"blur(10px)",border:"1.5px solid rgba(255,255,255,0.2)",borderRadius:16,fontSize:k==="⌫"?20:24,fontFamily:G.display,color:"#fff",cursor:"pointer",boxShadow:"0 2px 8px rgba(0,0,0,0.15)"}}>
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── HOME ──
function Home({ binders, onCreate, onOpen }) {
  const [showNew,setShowNew]=useState(false);
  const [step,setStep]=useState("type");
  const [pickedType,setPickedType]=useState(null);
  const [pickedSub,setPickedSub]=useState(null);
  const [name,setName]=useState("");

  const cType = UNIVERSE.find(u=>u.id===pickedType);
  const cSub  = cType?.subtypes?.find(s=>s.id===pickedSub);

  function create() {
    if(!cType||!cSub) return;
    const b={id:uid(),title:name.trim()||cSub.label,type:pickedType,subtype:pickedSub,
      icon:cSub.icon||cType.icon,color:cType.color,bg:cType.bg,
      categories:cSub.cats.map(c=>({...c,id:uid()})),createdAt:new Date().toISOString()};
    onCreate(b); reset();
  }
  function reset(){setShowNew(false);setStep("type");setPickedType(null);setPickedSub(null);setName("");}

  const byType={};
  binders.forEach(b=>{byType[b.type]=(byType[b.type]||[]).concat(b);});
  const totalEntries = binders.reduce((a,b)=>{const l=db.get(`gg_log_${b.id}`,[]);return a+(Array.isArray(l)?l.length:0);},0);

  return (
    <div style={{...S.page,paddingBottom:40}}>
      {/* HERO */}
      <div style={{background:heroGrad,padding:"0 0 0 0",position:"relative",overflow:"hidden"}}>
        {/* nav */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px 14px"}}>
          <Logo size={22}/>
          <button style={{...S.btnGold,padding:"8px 18px",fontSize:12,borderRadius:20}}>Get Started</button>
        </div>
        {/* sparkle dots */}
        {[[10,15],[380,40],[200,20],[350,70],[50,80]].map(([x,y],i)=>(
          <div key={i} style={{position:"absolute",left:x,top:y,width:5,height:5,borderRadius:"50%",background:"rgba(255,255,255,0.25)"}}/>
        ))}
        <div style={{padding:"8px 20px 0",position:"relative"}}>
          <div style={{fontFamily:G.display,fontSize:30,color:G.white,lineHeight:1.15,marginBottom:10,letterSpacing:0.3}}>
            Organize &amp; Protect<br/>
            <span style={{color:G.gold}}>Your Family's</span><br/>
            Legal Files
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.6,marginBottom:18,maxWidth:260}}>
            Collect and organize important documents, messages, and evidence in a secure, easy-to-use digital binder.
          </div>
          <button onClick={()=>setShowNew(true)} style={{...S.btnGold,padding:"13px 28px",fontSize:15,borderRadius:30,marginBottom:24}}>
            🚀 Get Started
          </button>
        </div>
        {/* wave bottom */}
        <Wave color={G.bg}/>
      </div>

      {/* STATS STRIP */}
      {binders.length>0&&(
        <div style={{display:"flex",background:G.white,borderRadius:20,margin:"0 16px 16px",boxShadow:cardShadow,overflow:"hidden",border:`1.5px solid ${G.line}`}}>
          {[["📁","Binders",binders.length],["📝","Entries",totalEntries],["🗂️","Types",Object.keys(byType).length]].map(([icon,l,v],i)=>(
            <div key={l} style={{flex:1,textAlign:"center",padding:"14px 8px",borderRight:i<2?`1.5px solid ${G.line}`:"none"}}>
              <div style={{fontSize:20,marginBottom:2}}>{icon}</div>
              <div style={{fontFamily:G.display,fontSize:22,color:G.blue,lineHeight:1}}>{v}</div>
              <div style={{fontSize:9,color:G.ink3,fontWeight:800,letterSpacing:1,textTransform:"uppercase",marginTop:2}}>{l}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{padding:"0 16px"}}>
        {/* Empty state */}
        {binders.length===0&&!showNew&&(
          <div className="fade-up" style={{...S.card,textAlign:"center",padding:36,borderStyle:"dashed",borderColor:G.gold,borderWidth:2}}>
            <div className="float" style={{fontSize:52,marginBottom:12}}>🛡️</div>
            <div style={{fontFamily:G.display,fontSize:22,color:G.blue,marginBottom:8}}>Your binders live here</div>
            <div style={{color:G.ink3,fontSize:13,lineHeight:1.6,marginBottom:18}}>Create your first binder to start organizing family law evidence, medical records, and more.</div>
            <button onClick={()=>setShowNew(true)} style={{...S.btnGold,justifyContent:"center",width:"100%",padding:"14px",fontSize:15,borderRadius:30}}>
              + Create First Binder
            </button>
          </div>
        )}

        {/* Binder cards */}
        {Object.entries(byType).map(([type,list])=>{
          const typeInfo = UNIVERSE.find(u=>u.id===type);
          return (
            <div key={type} style={{marginBottom:4}}>
              <div style={{fontSize:10,color:G.ink3,letterSpacing:2,fontWeight:800,textTransform:"uppercase",padding:"10px 2px 6px"}}>
                {typeInfo?.icon} {typeInfo?.label||type}
              </div>
              {list.map(b=>{
                const entries=(()=>{const l=db.get(`gg_log_${b.id}`,[]);return Array.isArray(l)?l.length:0;})();
                return (
                  <div key={b.id} className="fade-up" onClick={()=>onOpen(b.id)}
                    style={{...S.card,cursor:"pointer",display:"flex",alignItems:"center",gap:13,padding:"14px 16px",transition:"box-shadow .2s"}}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow=cardShadowHover}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow=cardShadow}>
                    <div style={{width:50,height:50,background:b.color+"1A",borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0,border:`2px solid ${b.color}22`}}>
                      {b.icon}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:G.display,fontSize:17,color:G.ink,marginBottom:2,letterSpacing:0.2}}>{b.title}</div>
                      <div style={{fontSize:11,color:G.ink3,fontWeight:600}}>{entries} entries · {fd(b.createdAt)}</div>
                    </div>
                    <div style={{width:28,height:28,background:G.blue+"12",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",color:G.blue,fontSize:14,fontWeight:800}}>›</div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {!showNew&&binders.length>0&&(
          <button onClick={()=>setShowNew(true)} style={{...S.btnGold,width:"100%",justifyContent:"center",padding:"14px",fontSize:15,borderRadius:20,marginTop:6}}>
            ＋ New Binder
          </button>
        )}

        {/* ── CREATE FLOW ── */}
        {showNew&&step==="type"&&(
          <div className="fade-up" style={{...S.card,borderColor:G.gold,borderWidth:2}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <span style={{fontSize:22}}>🛡️</span>
              <div style={{fontFamily:G.display,fontSize:20,color:G.blue}}>New Binder</div>
            </div>
            <div style={{color:G.ink3,fontSize:12,fontWeight:600,marginBottom:16}}>What are you tracking?</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
              {UNIVERSE.map(u=>(
                <div key={u.id} onClick={()=>{setPickedType(u.id);setStep("subtype");}}
                  style={{background:u.bg,border:`2px solid ${u.color}22`,borderRadius:16,padding:"14px 10px",cursor:"pointer",transition:"all .15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.border=`2px solid ${u.color}88`;e.currentTarget.style.transform="scale(1.02)";}}
                  onMouseLeave={e=>{e.currentTarget.style.border=`2px solid ${u.color}22`;e.currentTarget.style.transform="scale(1)";}}>
                  <div style={{fontSize:26,marginBottom:5}}>{u.icon}</div>
                  <div style={{fontSize:13,fontWeight:800,color:G.ink,marginBottom:2}}>{u.label}</div>
                  <div style={{fontSize:10,color:G.ink3,lineHeight:1.3,fontWeight:600}}>{u.desc}</div>
                </div>
              ))}
            </div>
            <button onClick={reset} style={{...S.btnGhost,width:"100%",justifyContent:"center"}}>Cancel</button>
          </div>
        )}

        {showNew&&step==="subtype"&&cType&&(
          <div className="fade-up" style={{...S.card,borderColor:cType.color+"44",borderWidth:2}}>
            <button onClick={()=>setStep("type")} style={{background:"none",border:"none",color:G.ink3,fontSize:12,cursor:"pointer",marginBottom:12,display:"flex",alignItems:"center",gap:4,padding:0,fontWeight:700}}>← Back</button>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,paddingBottom:12,borderBottom:`2px dashed ${G.line}`}}>
              <div style={{width:44,height:44,background:cType.color+"1A",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{cType.icon}</div>
              <div>
                <div style={{fontFamily:G.display,fontSize:18,color:G.ink}}>{cType.label}</div>
                <div style={{color:G.ink3,fontSize:11,fontWeight:600}}>{cType.desc}</div>
              </div>
            </div>
            {cType.subtypes.map(sub=>(
              <div key={sub.id} onClick={()=>{setPickedSub(sub.id);setStep("name");}}
                style={{background:G.bg,border:`2px solid ${G.line}`,borderRadius:16,padding:"12px 14px",marginBottom:8,cursor:"pointer",display:"flex",alignItems:"flex-start",gap:11,transition:"all .15s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=cType.color+"66";e.currentTarget.style.background=cType.bg;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=G.line;e.currentTarget.style.background=G.bg;}}>
                <div style={{width:36,height:36,background:cType.color+"1A",borderRadius:11,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{sub.icon}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:800,color:G.ink,marginBottom:2}}>{sub.label}</div>
                  <div style={{fontSize:11,color:G.ink3,fontWeight:600,marginBottom:8}}>{sub.desc}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {sub.cats.slice(0,4).map((c,i)=>(
                      <span key={i} style={S.badge(c.color)}>{c.icon} {c.label}</span>
                    ))}
                    {sub.cats.length>4&&<span style={{...S.badge(G.ink3)}}  >+{sub.cats.length-4}</span>}
                  </div>
                </div>
              </div>
            ))}
            <button onClick={reset} style={{...S.btnGhost,width:"100%",justifyContent:"center",marginTop:4}}>Cancel</button>
          </div>
        )}

        {showNew&&step==="name"&&cType&&cSub&&(
          <div className="fade-up" style={{...S.card,borderColor:cType.color+"44",borderWidth:2}}>
            <button onClick={()=>setStep("subtype")} style={{background:"none",border:"none",color:G.ink3,fontSize:12,cursor:"pointer",marginBottom:12,display:"flex",alignItems:"center",gap:4,padding:0,fontWeight:700}}>← Back</button>
            <div style={{background:cType.color+"10",border:`2px solid ${cType.color}22`,borderRadius:16,padding:"14px",marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <div style={{width:42,height:42,background:cType.color+"22",borderRadius:13,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{cSub.icon}</div>
                <div>
                  <div style={{fontFamily:G.display,fontSize:16,color:G.ink}}>{cSub.label}</div>
                  <div style={{fontSize:10,color:cType.color,letterSpacing:1.5,textTransform:"uppercase",fontWeight:800}}>{cType.label}</div>
                </div>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                {cSub.cats.map((c,i)=><span key={i} style={S.badge(c.color)}>{c.icon} {c.label}</span>)}
              </div>
            </div>
            <span style={S.lbl}>Binder Name <span style={{color:G.ink3,textTransform:"none",fontSize:10}}>(optional)</span></span>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder={cSub.label}
              style={{...S.inp,marginBottom:14}} onKeyDown={e=>e.key==="Enter"&&create()}/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={create} style={{...S.btnGold,flex:1,justifyContent:"center",borderRadius:14}}>Create Binder 🎉</button>
              <button onClick={reset} style={S.btnGhost}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Features strip */}
      {binders.length===0&&!showNew&&(
        <div style={{padding:"0 16px",marginTop:8}}>
          <Wave color={G.white} flip/>
          <div style={{background:G.white,padding:"24px 16px 20px",marginTop:-1}}>
            <div style={{fontFamily:G.display,fontSize:22,color:G.ink,textAlign:"center",marginBottom:6}}>Stay Prepared &amp; Document Everything</div>
            <div style={{color:G.ink3,fontSize:12,textAlign:"center",lineHeight:1.6,marginBottom:20,fontWeight:600}}>
              Keep texts, photos, documents, and evidence organized. Whether you're in a custody battle or just staying prepared, Gotcha Guard is here.
            </div>
            {[
              {icon:"📤",title:"Upload Evidence Easily",desc:"Quickly upload texts, photos, videos, and documents straight from your phone."},
              {icon:"🤖",title:"AI-Assisted Organization",desc:"Our smart tool organizes, tags, and arranges your evidence into a clear timeline."},
              {icon:"📋",title:"In Case You Need It",desc:"Easily export a clear, organized binder when it's time for court or sharing with your attorney."},
            ].map((f,i)=>(
              <div key={i} style={{...S.card,display:"flex",gap:12,alignItems:"flex-start",padding:"14px"}}>
                <div style={{width:44,height:44,background:`linear-gradient(135deg,${G.blue3},${G.card})`,borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0,border:`2px solid ${G.line}`}}>{f.icon}</div>
                <div>
                  <div style={{fontFamily:G.display,fontSize:15,color:G.ink,marginBottom:3}}>{f.title}</div>
                  <div style={{fontSize:12,color:G.ink3,lineHeight:1.55,fontWeight:600}}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <Wave color={G.bg}/>
        </div>
      )}
    </div>
  );
}

// ── BINDER VIEW ──
function BinderView({ binder, onBack, onUpdate }) {
  const bid = binder.id;
  const ac  = binder.color || G.blue;
  const tabs = TYPE_TABS[binder.type] || TYPE_TABS.custom;

  const [tab,setTab]=useState(tabs[0]);
  const role = TAB_ROLE[tab]||"log";
  const meta = TAB_META[tab]||{lbl:"Category",ph:"Describe this entry…"};

  const typeInfo    = UNIVERSE.find(u=>u.id===binder.type);
  const subtypeInfo = typeInfo?.subtypes?.find(s=>s.id===binder.subtype);

  const [cats,setCats]=useState(()=>binder.categories||[]);
  const [log,setLog]=useState(()=>{const v=db.get(`gg_log_${bid}`,[]);return Array.isArray(v)?v:[];});
  const [ev,setEv]  =useState(()=>{const v=db.get(`gg_ev_${bid}`,[]);return Array.isArray(v)?v:[];});
  const [docs,setDocs]=useState(()=>{const v=db.get(`gg_doc_${bid}`,[]);return Array.isArray(v)?v:[];});

  const [form,setForm]=useState({catId:"",date:new Date().toISOString().slice(0,10),time:new Date().toTimeString().slice(0,5),text:"",tags:""});
  const [addCat,setAddCat]=useState(false);
  const [newCat,setNewCat]=useState({label:"",icon:"📌"});
  const [busy,setBusy]=useState(false);
  const [link,setLink]=useState("");
  const [rawText,setRaw]=useState("");
  const [docResult,setDocResult]=useState("");
  const [docBusy,setDocBusy]=useState(false);
  const [docSaved,setDocSaved]=useState(false);
  const [aiQ,setAiQ]=useState("");
  const [aiA,setAiA]=useState("");
  const [aiBusy,setAiBusy]=useState(false);
  const [expMsg,setExpMsg]=useState("");
  const fRef=useRef();

  useEffect(()=>db.set(`gg_log_${bid}`,log),[log]);
  useEffect(()=>db.set(`gg_ev_${bid}`,ev),[ev]);
  useEffect(()=>db.set(`gg_doc_${bid}`,docs),[docs]);
  useEffect(()=>onUpdate({...binder,categories:cats}),[cats]);

  function addEntry(){
    if(!form.catId)return;
    const cat=cats.find(c=>c.id===form.catId);
    const e={id:uid(),catId:form.catId,cat:cat?.label,catIcon:cat?.icon,catColor:cat?.color,
      date:form.date,time:form.time,text:form.text,
      tags:form.tags.split(",").map(t=>t.trim()).filter(Boolean),
      createdAt:new Date().toISOString()};
    setLog(p=>[e,...p]);
    setForm(f=>({...f,catId:"",text:"",tags:""}));
  }

  function saveCat(){
    if(!newCat.label.trim())return;
    setCats(p=>[...p,{id:uid(),label:newCat.label,icon:newCat.icon,color:G.gold}]);
    setNewCat({label:"",icon:"📌"}); setAddCat(false);
  }

  async function handleFile(e){
    const file=e.target.files[0];if(!file)return;
    setBusy(true);
    const r=new FileReader();
    r.onload=async ev2=>{
      const b64=ev2.target.result.split(",")[1];const mt=file.type||"image/jpeg";
      setEv(p=>[{id:uid(),type:"file",name:file.name,mediaType:mt,base64:b64,date:new Date().toISOString(),summary:"File saved."},...p]);
      setBusy(false);
    };
    r.readAsDataURL(file); e.target.value="";
  }

  async function handleLink(){
    if(!link.trim())return;
    setBusy(true);
    setEv(p=>[{id:uid(),type:"link",name:link,url:link,date:new Date().toISOString(),summary:"Link saved."},...p]);
    setLink(""); setBusy(false);
  }

  async function generateDoc(){
    if(!rawText.trim())return;
    setDocBusy(true);setDocResult("");setDocSaved(false);
    const r=await voiceToDoc(rawText,binder.type,binder.title,log);
    setDocResult(r);setDocBusy(false);
  }

  function saveDoc(){
    if(!docResult)return;
    setDocs(p=>[{id:uid(),title:`Document — ${fd(new Date())}`,content:docResult,rawInput:rawText,createdAt:new Date().toISOString()},...p]);
    setDocSaved(true);
  }

  async function handleAsk(){
    if(!aiQ.trim())return;
    setAiBusy(true);
    const a=await askAI(aiQ,log,binder.type,binder.title);
    setAiA(a);setAiBusy(false);
  }

  function exportBinder(){
    const data=JSON.stringify({binder:{...binder,categories:cats},log,evidence:ev,documents:docs},null,2);
    const blob=new Blob([data],{type:"application/json"});
    const a=document.createElement("a");a.href=URL.createObjectURL(blob);
    a.download=`${binder.title.replace(/\s+/g,"-")}.json`;a.click();
    setExpMsg("Downloaded! ✓"); setTimeout(()=>setExpMsg(""),3000);
  }

  const catCounts={};
  cats.forEach(c=>{catCounts[c.id]=0;});
  log.forEach(e=>{if(catCounts[e.catId]!==undefined)catCounts[e.catId]++;});
  const pieData=cats.filter(c=>catCounts[c.id]>0).map(c=>({name:c.label,value:catCounts[c.id],color:c.color||ac}));
  const monthly={};
  log.forEach(e=>{const m=e.date?.slice(0,7)||"?";monthly[m]=(monthly[m]||0)+1;});
  const barData=Object.entries(monthly).sort().map(([month,count])=>({month,count}));

  const EC = { background:G.card, border:`1.5px solid ${G.line}`, borderRadius:16, padding:14, marginBottom:10, boxShadow:"0 2px 10px rgba(21,101,192,0.07)" };

  return (
    <div style={S.page}>
      {/* HEADER */}
      <div style={{background:heroGrad,paddingBottom:0,position:"sticky",top:0,zIndex:20}}>
        {/* top bar */}
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px 10px"}}>
          <button onClick={onBack} style={{background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:4,padding:"6px 12px",borderRadius:20,fontWeight:700,fontFamily:G.body}}>
            ← Back
          </button>
          <div style={{flex:1,display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:36,height:36,background:"rgba(255,255,255,0.18)",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{binder.icon}</div>
            <div>
              <div style={{fontFamily:G.display,fontSize:16,color:G.white,lineHeight:1,letterSpacing:0.2}}>{binder.title}</div>
              <div style={{fontSize:9.5,color:"rgba(255,255,255,0.65)",fontWeight:700,marginTop:1}}>
                {typeInfo?.label}{subtypeInfo?` · ${subtypeInfo.label}`:""} · {log.length} entries
              </div>
            </div>
          </div>
        </div>
        {/* tabs */}
        <div style={{display:"flex",overflowX:"auto",WebkitOverflowScrolling:"touch",background:"rgba(0,0,0,0.15)",borderTop:"1px solid rgba(255,255,255,0.1)"}}>
          {tabs.map(t=>(
            <button key={t} onClick={()=>setTab(t)}
              style={{flex:1,padding:"10px 8px",textAlign:"center",fontSize:9,letterSpacing:0.3,fontWeight:800,background:"none",border:"none",cursor:"pointer",color:tab===t?"#fff":"rgba(255,255,255,0.55)",borderBottom:tab===t?`3px solid ${G.gold}`:"3px solid transparent",whiteSpace:"nowrap",minWidth:60,fontFamily:G.body,transition:"all .15s"}}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* ── JOURNAL ── */}
      {role==="journal"&&<div style={S.sec}>
        <div style={S.lbl}>Recent Activity</div>
        {log.length===0&&ev.length===0&&docs.length===0&&(
          <div style={{...EC,textAlign:"center",padding:36,borderStyle:"dashed",borderColor:G.gold,borderWidth:2}}>
            <div className="float" style={{fontSize:40,marginBottom:10}}>{binder.icon}</div>
            <div style={{fontFamily:G.display,fontSize:17,color:G.ink,marginBottom:6}}>No entries yet</div>
            <div style={{color:G.ink3,fontSize:12,fontWeight:600,lineHeight:1.5}}>Use the tabs above to add entries, upload files, and generate documents.</div>
          </div>
        )}
        {[...log.slice(0,5).map(e=>({...e,_t:"log"})),...ev.slice(0,3).map(e=>({...e,_t:"ev"})),...docs.slice(0,2).map(e=>({...e,_t:"doc"}))]
          .sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).slice(0,8).map(item=>{
          if(item._t==="log") return(
            <div key={item.id} style={{...EC,borderLeft:`4px solid ${item.catColor||ac}`,borderTopLeftRadius:4,borderBottomLeftRadius:4}}>
              <div style={{display:"flex",alignItems:"center",marginBottom:item.text?5:0}}>
                <span style={{fontSize:18,marginRight:9}}>{item.catIcon}</span>
                <span style={{...S.badge(item.catColor||ac),flex:1}}>{item.cat}</span>
                <span style={{color:G.ink3,fontSize:10,fontWeight:700}}>{fd(item.date)}</span>
              </div>
              {item.text&&<div style={{color:G.ink2,fontSize:12,lineHeight:1.6,paddingLeft:27,fontWeight:600}}>{item.text}</div>}
            </div>
          );
          if(item._t==="ev") return(
            <div key={item.id} style={{...EC,display:"flex",gap:10}}>
              {item.type==="file"&&item.mediaType?.startsWith("image/")
                ?<img src={`data:${item.mediaType};base64,${item.base64}`} style={{width:46,height:46,borderRadius:12,objectFit:"cover"}} alt=""/>
                :<div style={{width:46,height:46,background:ac+"15",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{item.type==="link"?"🔗":"📎"}</div>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{color:ac,fontSize:11,fontWeight:800,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                <div style={{color:G.ink3,fontSize:10,fontWeight:600}}>{item.summary}</div>
              </div>
            </div>
          );
          if(item._t==="doc") return(
            <div key={item.id} style={{...EC,borderLeft:`4px solid ${G.gold}`}}>
              <div style={{display:"flex",alignItems:"center"}}>
                <span style={{fontSize:18,marginRight:9}}>📄</span>
                <span style={{...S.badge(G.gold),flex:1}}>Document</span>
                <span style={{color:G.ink3,fontSize:10,fontWeight:700}}>{fd(item.createdAt)}</span>
              </div>
              <div style={{color:G.ink2,fontSize:12,fontFamily:G.display,marginTop:4,paddingLeft:27}}>{item.title}</div>
            </div>
          );
          return null;
        })}

        <hr style={S.div}/>
        <div style={S.lbl}>Ask AI About This Binder</div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <input style={S.inp} value={aiQ} onChange={e=>setAiQ(e.target.value)} placeholder="What patterns do you see? Am I documenting enough?"/>
          <button onClick={handleAsk} disabled={aiBusy} style={{...S.btnBlue,whiteSpace:"nowrap",padding:"11px 14px",borderRadius:12}}>
            {aiBusy?"…":"Ask 🤖"}
          </button>
        </div>
        {aiA&&<div style={{...EC,background:"#EEF5FF",borderColor:G.blue+"44",borderWidth:2}}>
          <div style={{...S.badge(G.blue),marginBottom:8}}>🤖 AI Insight</div>
          <div style={{color:G.ink,fontSize:12,lineHeight:1.75,fontWeight:600}}>{aiA}</div>
        </div>}
      </div>}

      {/* ── LOG ── */}
      {(role==="log")&&<div style={S.sec}>
        <span style={S.lbl}>{meta.lbl}</span>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
          {cats.map(c=>(
            <button key={c.id} onClick={()=>setForm(f=>({...f,catId:f.catId===c.id?"":c.id}))}
              style={{background:form.catId===c.id?c.color+"18":G.card,border:`2px solid ${form.catId===c.id?c.color:G.line}`,borderRadius:14,padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:12,fontWeight:800,color:form.catId===c.id?c.color:G.ink,transition:"all .15s",fontFamily:G.body,boxShadow:form.catId===c.id?`0 4px 12px ${c.color}33`:"none"}}>
              <span style={{fontSize:18}}>{c.icon}</span><span>{c.label}</span>
            </button>
          ))}
          <button onClick={()=>setAddCat(v=>!v)} style={{background:G.bg,border:`2px dashed ${G.line}`,borderRadius:14,padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:12,color:G.ink3,fontWeight:800,fontFamily:G.body}}>
            <span style={{fontSize:18}}>＋</span>Custom
          </button>
        </div>

        {addCat&&<div style={{...EC,marginBottom:14,borderColor:G.gold,borderWidth:2}}>
          <span style={S.lbl}>New Category</span>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <input style={{...S.inp,width:60,textAlign:"center"}} value={newCat.icon} onChange={e=>setNewCat(n=>({...n,icon:e.target.value}))} placeholder="📌"/>
            <input style={S.inp} value={newCat.label} onChange={e=>setNewCat(n=>({...n,label:e.target.value}))} placeholder="Category name"/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button style={{...S.btnGold,borderRadius:12}} onClick={saveCat}>Add</button>
            <button style={S.btnGhost} onClick={()=>setAddCat(false)}>Cancel</button>
          </div>
        </div>}

        <span style={S.lbl}>Date &amp; Time</span>
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          <input type="date" style={S.inp} value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))}/>
          <input type="time" style={{...S.inp,width:118}} value={form.time} onChange={e=>setForm(f=>({...f,time:e.target.value}))}/>
        </div>

        <span style={S.lbl}>Notes</span>
        <textarea style={{...S.txa,marginBottom:12}} value={form.text} onChange={e=>setForm(f=>({...f,text:e.target.value}))} placeholder={meta.ph}/>

        <span style={S.lbl}>Tags <span style={{color:G.ink3,textTransform:"none",letterSpacing:0,fontSize:10}}>(comma-separated)</span></span>
        <input style={{...S.inp,marginBottom:16}} value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} placeholder="tag1, tag2"/>

        <button disabled={!form.catId} onClick={addEntry} style={{...S.btnGold,width:"100%",justifyContent:"center",padding:"14px",marginBottom:20,borderRadius:16,fontSize:15,opacity:form.catId?1:0.45}}>
          📝 Add Entry
        </button>

        <hr style={S.div}/>
        <span style={S.lbl}>All Entries ({log.length})</span>
        {log.length===0&&<div style={{color:G.ink3,fontSize:13,padding:"14px 0",textAlign:"center",fontWeight:600}}>No entries yet — add one above.</div>}
        {log.slice(0,30).map(entry=>(
          <div key={entry.id} style={{...EC,borderLeft:`4px solid ${entry.catColor||ac}`}}>
            <div style={{display:"flex",alignItems:"center",marginBottom:entry.text?5:0}}>
              <span style={{fontSize:18,marginRight:9}}>{entry.catIcon}</span>
              <span style={{...S.badge(entry.catColor||ac),flex:1}}>{entry.cat}</span>
              <span style={{color:G.ink3,fontSize:10,fontWeight:700}}>{fd(entry.date)}{entry.time?" · "+entry.time:""}</span>
            </div>
            {entry.text&&<div style={{color:G.ink2,fontSize:12,lineHeight:1.65,paddingLeft:27,fontWeight:600}}>{entry.text}</div>}
            {entry.tags?.length>0&&<div style={{paddingLeft:27,marginTop:5,display:"flex",gap:4,flexWrap:"wrap"}}>
              {entry.tags.map((tg,i)=><span key={i} style={{background:G.blue3,color:G.blue,borderRadius:6,padding:"2px 7px",fontSize:9,fontWeight:800}}>#{tg}</span>)}
            </div>}
          </div>
        ))}
      </div>}

      {/* ── EVIDENCE ── */}
      {role==="evidence"&&<div style={S.sec}>
        <span style={S.lbl}>Upload Evidence</span>
        <div style={{border:`2.5px dashed ${G.gold}`,borderRadius:18,padding:26,textAlign:"center",cursor:"pointer",marginBottom:14,background:G.gold+"08"}} onClick={()=>fRef.current.click()}>
          <div style={{width:56,height:56,background:btnGrad,borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 10px",boxShadow:"0 4px 14px rgba(230,81,0,0.3)"}}>📤</div>
          <div style={{color:G.ink,fontSize:13,fontWeight:800,marginBottom:3}}>Tap to upload</div>
          <div style={{color:G.ink3,fontSize:11,fontWeight:600}}>Photos, screenshots, documents</div>
        </div>
        <input ref={fRef} type="file" accept="image/*,.pdf" style={{display:"none"}} onChange={handleFile}/>
        {busy&&<div style={{...EC,textAlign:"center",background:"#EEF5FF",borderColor:G.blue,borderWidth:2,marginBottom:14}}>Processing… ⏳</div>}

        <span style={S.lbl}>Save a Link</span>
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          <input style={S.inp} value={link} onChange={e=>setLink(e.target.value)} placeholder="https://…"/>
          <button style={{...S.btnBlue,whiteSpace:"nowrap",padding:"11px 16px",borderRadius:12}} onClick={handleLink}>Save</button>
        </div>

        <hr style={S.div}/>
        <span style={S.lbl}>Saved Files &amp; Links ({ev.length})</span>
        {ev.length===0&&<div style={{color:G.ink3,fontSize:13,padding:"14px 0",textAlign:"center",fontWeight:600}}>No files yet.</div>}
        {ev.map(item=>(
          <div key={item.id} style={{...EC,display:"flex",gap:11}}>
            {item.type==="file"&&item.mediaType?.startsWith("image/")
              ?<img src={`data:${item.mediaType};base64,${item.base64}`} style={{width:56,height:56,borderRadius:14,objectFit:"cover"}} alt=""/>
              :<div style={{width:56,height:56,background:ac+"15",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>{item.type==="link"?"🔗":"📄"}</div>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:ac,fontSize:11,fontWeight:800,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
              <div style={{color:G.ink3,fontSize:10,fontWeight:700,marginBottom:3}}>{fdt(item.date)}</div>
              {item.summary&&<div style={{color:G.ink2,fontSize:11,lineHeight:1.5,fontWeight:600}}>{item.summary}</div>}
              {item.url&&<a href={item.url} target="_blank" rel="noreferrer" style={{color:ac,fontSize:10,fontWeight:800}}>Open link →</a>}
            </div>
          </div>
        ))}
      </div>}

      {/* ── CHARTS ── */}
      {role==="charts"&&<div style={S.sec}>
        <div style={S.lbl}>Incident Breakdown</div>
        {pieData.length===0
          ?<div style={{color:G.ink3,fontSize:13,padding:"20px 0",textAlign:"center",fontWeight:600}}>No data yet.</div>
          :<>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={88} innerRadius={34} dataKey="value">
                  {pieData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                </Pie>
                <Tooltip contentStyle={{background:G.card,border:`1.5px solid ${G.line}`,color:G.ink,fontSize:11,borderRadius:10}}/>
              </PieChart>
            </ResponsiveContainer>
            {pieData.map(d=>(
              <div key={d.name} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                <div style={{width:12,height:12,borderRadius:"50%",background:d.color,flexShrink:0}}/>
                <span style={{color:G.ink2,fontSize:12,flex:1,fontWeight:700}}>{d.name}</span>
                <div style={{flex:2,height:6,background:G.bg,borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${(d.value/Math.max(log.length,1))*100}%`,background:d.color,borderRadius:4}}/>
                </div>
                <span style={{color:d.color,fontSize:16,fontWeight:900,minWidth:24,textAlign:"right"}}>{d.value}</span>
              </div>
            ))}
          </>}
        {barData.length>0&&<>
          <div style={{...S.lbl,marginTop:20}}>By Month</div>
          <div style={{...EC}}>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={barData}>
                <XAxis dataKey="month" stroke={G.line} tick={{fill:G.ink3,fontSize:9,fontFamily:G.body,fontWeight:700}}/>
                <YAxis stroke={G.line} tick={{fill:G.ink3,fontSize:9,fontFamily:G.body,fontWeight:700}} allowDecimals={false}/>
                <Tooltip contentStyle={{background:G.card,border:`1.5px solid ${G.line}`,color:G.ink,fontSize:11,borderRadius:10}}/>
                <Bar dataKey="count" fill={ac} radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>}
      </div>}

      {/* ── VOICE→DOC ── */}
      {role==="voice"&&<div style={S.sec}>
        <div style={{background:heroGrad,borderRadius:18,padding:"18px 16px",marginBottom:16,boxShadow:"0 4px 18px rgba(21,101,192,0.3)"}}>
          <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <div style={{width:40,height:40,background:btnGrad,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,boxShadow:"0 3px 10px rgba(230,81,0,0.4)"}}>✍️</div>
            <div>
              <div style={{fontFamily:G.display,fontSize:17,color:G.white,marginBottom:4}}>Voice → Document</div>
              <div style={{color:"rgba(255,255,255,0.75)",fontSize:12,lineHeight:1.6,fontWeight:600}}>
                Type freely — what happened, how you feel. AI rewrites it as a professional {binder.type==="legal"?"court-ready declaration":"formatted document"}.
              </div>
            </div>
          </div>
        </div>

        <span style={S.lbl}>Say What's Happening</span>
        <textarea style={{...S.txa,minHeight:140,marginBottom:14}} value={rawText} onChange={e=>setRaw(e.target.value)}
          placeholder={binder.type==="legal"?"Tell me everything — what they did, when, how it affected you. Don't worry about format or structure…"
            :"Tell me everything about this situation in your own words…"}/>
        <button onClick={generateDoc} disabled={docBusy||!rawText.trim()} style={{...S.btnGold,width:"100%",justifyContent:"center",padding:"14px",marginBottom:14,borderRadius:16,fontSize:15,opacity:(!rawText.trim()||docBusy)?0.5:1}}>
          {docBusy?"⟳ Writing Document…":"✦ Generate Professional Document"}
        </button>

        {docResult&&(
          <div className="fade-up" style={{...EC,borderColor:G.gold,borderWidth:2}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <span style={S.badge(G.gold)}>📄 Generated Document</span>
              <div style={{display:"flex",gap:7}}>
                {!docSaved&&<button onClick={saveDoc} style={{background:G.gold+"22",color:G.goldD,border:"none",borderRadius:8,padding:"5px 11px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:G.body}}>Save</button>}
                {docSaved&&<span style={{color:G.green,fontSize:10,fontWeight:800}}>✓ Saved</span>}
                <button onClick={()=>{const blob=new Blob([docResult],{type:"text/plain"});const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download="document.txt";a.click();}} style={{background:G.blue3,color:G.blue,border:"none",borderRadius:8,padding:"5px 11px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:G.body}}>Download</button>
              </div>
            </div>
            <div style={{background:G.bg,borderRadius:12,padding:"14px 16px",border:`1.5px solid ${G.line}`,color:G.ink,fontSize:12,lineHeight:1.8,whiteSpace:"pre-wrap",fontWeight:600}}>{docResult}</div>
            <button onClick={()=>{setDocResult("");setRaw("");setDocSaved(false);}} style={{background:"none",border:"none",color:G.ink3,fontSize:11,cursor:"pointer",marginTop:8,fontWeight:700,fontFamily:G.body}}>Clear</button>
          </div>
        )}

        {docs.length>0&&<>
          <hr style={S.div}/>
          <span style={S.lbl}>Saved Documents ({docs.length})</span>
          {docs.map(doc=>(
            <div key={doc.id} style={EC}>
              <div style={{fontFamily:G.display,fontSize:14,color:G.ink,marginBottom:2}}>{doc.title}</div>
              <div style={{color:G.ink3,fontSize:10,fontWeight:700,marginBottom:6}}>{fdt(doc.createdAt)}</div>
              <div style={{color:G.ink2,fontSize:11,lineHeight:1.65,maxHeight:60,overflow:"hidden",fontWeight:600}}>{doc.content?.slice(0,180)}…</div>
            </div>
          ))}
        </>}
      </div>}

      {/* ── REPORT ── */}
      {role==="report"&&<div style={S.sec}>
        <div style={{background:heroGrad,borderRadius:20,padding:"22px 20px",marginBottom:16,position:"relative",overflow:"hidden",boxShadow:"0 6px 22px rgba(21,101,192,0.3)"}}>
          <div style={{position:"absolute",top:-40,right:-40,width:120,height:120,borderRadius:"50%",background:"rgba(245,166,35,0.18)"}}/>
          <div style={{fontSize:9,letterSpacing:2.5,color:"rgba(255,255,255,0.55)",fontWeight:800,textTransform:"uppercase",marginBottom:4}}>Summary Report</div>
          <div style={{fontFamily:G.display,color:"#fff",fontSize:22,letterSpacing:0.3,marginBottom:3}}>{binder.title}</div>
          <div style={{color:"rgba(255,255,255,0.55)",fontSize:10,fontWeight:600,marginBottom:16}}>{new Date().toLocaleDateString()}</div>
          <div style={{display:"flex",gap:20}}>
            {[["Entries",log.length],["Files",ev.length],["Docs",docs.length]].map(([l,v])=>(
              <div key={l}>
                <div style={{fontFamily:G.display,fontSize:26,color:G.gold,lineHeight:1}}>{v}</div>
                <div style={{fontSize:9,color:"rgba(255,255,255,0.55)",letterSpacing:2,textTransform:"uppercase",fontWeight:800}}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {pieData.length>0&&<>
          <span style={S.lbl}>Category Breakdown</span>
          {pieData.map(d=>(
            <div key={d.name} style={{...EC,display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:d.color}}/>
              <span style={{color:G.ink,flex:1,fontSize:12,fontWeight:800}}>{d.name}</span>
              <div style={{flex:2,height:7,background:G.bg,borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${(d.value/Math.max(log.length,1))*100}%`,background:d.color,borderRadius:4}}/>
              </div>
              <span style={{color:d.color,fontSize:18,fontWeight:900,minWidth:24,textAlign:"right"}}>{d.value}</span>
            </div>
          ))}
        </>}

        {barData.length>0&&<>
          <span style={{...S.lbl,marginTop:20}}>By Month</span>
          <div style={EC}>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={barData}>
                <XAxis dataKey="month" stroke={G.line} tick={{fill:G.ink3,fontSize:9,fontFamily:G.body,fontWeight:700}}/>
                <YAxis stroke={G.line} tick={{fill:G.ink3,fontSize:9,fontFamily:G.body,fontWeight:700}} allowDecimals={false}/>
                <Tooltip contentStyle={{background:G.card,border:`1.5px solid ${G.line}`,color:G.ink,fontSize:11,borderRadius:10}}/>
                <Bar dataKey="count" fill={ac} radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>}

        <hr style={S.div}/>
        <span style={S.lbl}>Full Log</span>
        {[...log].sort((a,b)=>new Date(a.date)-new Date(b.date)).map(entry=>(
          <div key={entry.id} style={{...EC,borderLeft:`4px solid ${entry.catColor||ac}`}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:entry.text?3:0}}>
              <span style={S.badge(entry.catColor||ac)}>{entry.catIcon} {entry.cat}</span>
              <span style={{color:G.ink3,fontSize:10,fontWeight:700}}>{fd(entry.date)}</span>
            </div>
            {entry.text&&<div style={{color:G.ink2,fontSize:11,lineHeight:1.6,fontWeight:600,marginTop:4}}>{entry.text}</div>}
          </div>
        ))}

        <button onClick={exportBinder} style={{...S.btnGold,width:"100%",justifyContent:"center",padding:"14px",fontSize:15,borderRadius:16,marginTop:16,marginBottom:4}}>
          📥 Export Organized Binder
        </button>
        {expMsg&&<div style={{color:G.green,fontSize:12,textAlign:"center",fontWeight:800,marginBottom:8}}>{expMsg}</div>}
      </div>}

      {/* ── SETTINGS ── */}
      {role==="settings"&&<div style={S.sec}>
        <span style={S.lbl}>Backup &amp; Restore</span>
        <div style={EC}>
          <div style={{color:G.ink2,fontSize:12,fontWeight:600,marginBottom:14,lineHeight:1.6}}>Export your binder as JSON to back it up or share with your attorney.</div>
          <button style={{...S.btnGold,borderRadius:12}} onClick={exportBinder}>📥 Export Binder</button>
          {expMsg&&<div style={{color:G.green,fontSize:11,marginTop:10,fontWeight:800}}>{expMsg}</div>}
        </div>

        <hr style={S.div}/>
        <span style={S.lbl}>Categories</span>
        {cats.map(c=>(
          <div key={c.id} style={{...EC,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:20}}>{c.icon}</span>
            <span style={{flex:1,fontSize:12,color:G.ink,fontWeight:800}}>{c.label}</span>
            <div style={{width:10,height:10,borderRadius:"50%",background:c.color||ac}}/>
            <button onClick={()=>setCats(p=>p.filter(x=>x.id!==c.id))} style={{background:G.red+"15",color:G.red,border:"none",borderRadius:8,padding:"5px 11px",fontSize:9,fontWeight:800,cursor:"pointer",fontFamily:G.body}}>Remove</button>
          </div>
        ))}
        <button onClick={()=>setAddCat(true)} style={{...S.btnGhost,width:"100%",justifyContent:"center",marginTop:6}}>+ Add Category</button>

        {addCat&&<div style={{...EC,marginTop:10,borderColor:G.gold,borderWidth:2}}>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <input style={{...S.inp,width:60,textAlign:"center"}} value={newCat.icon} onChange={e=>setNewCat(n=>({...n,icon:e.target.value}))} placeholder="📌"/>
            <input style={S.inp} value={newCat.label} onChange={e=>setNewCat(n=>({...n,label:e.target.value}))} placeholder="Category name"/>
          </div>
          <button style={{...S.btnGold,borderRadius:12}} onClick={saveCat}>Add</button>
        </div>}

        <hr style={S.div}/>
        <span style={S.lbl}>Install as App</span>
        <div style={{...EC,lineHeight:2}}>
          <div style={{color:G.ink2,fontSize:12,fontWeight:700}}>📱 <strong style={{color:ac}}>iOS:</strong> Safari → Share → "Add to Home Screen"</div>
          <div style={{color:G.ink2,fontSize:12,fontWeight:700}}>🤖 <strong style={{color:ac}}>Android:</strong> Chrome → Menu → "Add to Home Screen"</div>
        </div>

        <hr style={S.div}/>
        <div style={EC}>
          <div style={{color:G.ink3,fontSize:11,fontWeight:700,marginBottom:10,lineHeight:1.5}}>⚠️ Permanently delete all data in this binder.</div>
          <button onClick={()=>{if(window.confirm("Delete all entries?")) {db.del(`gg_log_${bid}`);db.del(`gg_ev_${bid}`);db.del(`gg_doc_${bid}`);setLog([]);setEv([]);setDocs([]);}}}
            style={{background:G.red+"12",color:G.red,border:`1.5px solid ${G.red}22`,borderRadius:10,padding:"10px 16px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:G.body}}>
            🗑️ Clear All Entries
          </button>
        </div>
        <div style={{height:40}}/>
      </div>}
    </div>
  );
}

// ── ROOT ──
export default function App() {
  const savedPin = db.get("gg_pin",null);
  const [unlocked,setUnlocked]=useState(!savedPin);
  const [binders,setBinders]=useState(()=>db.get("gg_binders",[])||[]);
  const [activeId,setActiveId]=useState(null);

  useEffect(()=>db.set("gg_binders",binders),[binders]);
  const active = binders.find(b=>b.id===activeId);

  if (!unlocked) return <Pin isSetup={!savedPin} onUnlock={()=>setUnlocked(true)}/>;
  if (active) return (
    <BinderView
      binder={active}
      onBack={()=>setActiveId(null)}
      onUpdate={u=>setBinders(p=>p.map(b=>b.id===u.id?u:b))}
    />
  );
  return (
    <Home
      binders={binders}
      onCreate={b=>{setBinders(p=>[...p,b]);setActiveId(b.id);}}
      onOpen={id=>setActiveId(id)}
    />
  );
}
